import { useEffect } from 'react';
import { Stack, Group, Card, Title, Text, Button, TextInput, ActionIcon, Center, SimpleGrid } from '@mantine/core';
import { IconArrowLeft, IconTrash, IconDeviceFloppy, IconPencil } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { confirmDelete } from '../lib/confirm';
import { SubjectIconPicker, SubjectIconView } from '../components/SubjectIcon';
import { SectionCard } from '../components/SectionCard';
import { BlockLoader } from '../components/BlockLoader';

interface SubjectDetailData {
  id: string; name: string; code?: string | null; nameRu?: string | null; nameEn?: string | null;
  /** T4: admin tanlagan ikonka kaliti (null — tanlanmagan) */
  icon?: string | null;
  /** T4: fan nomiga qarab server taklifi — DOIM to'ldirilgan */
  suggestedIcon?: string;
  _count?: { schedule: number; homeworks: number };
}

const apiErr = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** Fan detali — ma'lumot + tahrirlash + o'chirish (bir sahifada). */
export default function SubjectDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: subject, isLoading } = useQuery({
    queryKey: ['subject', id],
    queryFn: async () => (await api.get<SubjectDetailData>(`/subjects/${id}`)).data,
    enabled: !!id,
  });

  const form = useForm({
    // `icon: null` — "standart" holat: DB'da NULL, ko'rinishda esa nomga qarab taklif.
    initialValues: { name: '', code: '', nameRu: '', nameEn: '', icon: null as string | null },
    validate: { name: (v) => (!v ? t('qc.subjectNameRequired') : null) },
  });
  useEffect(() => {
    if (subject) form.setValues({ name: subject.name, code: subject.code ?? '', nameRu: subject.nameRu ?? '', nameEn: subject.nameEn ?? '', icon: subject.icon ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject?.id]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['subject', id] });
    qc.invalidateQueries({ queryKey: ['subjects'] });
    qc.invalidateQueries({ queryKey: ['subjects-page'] });
  };

  const saveMut = useMutation({
    mutationFn: (v: typeof form.values) => api.patch(`/subjects/${id}`, v),
    onSuccess: () => { notifications.show({ color: 'green', message: t('common.save') }); invalidate(); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/subjects/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('subjectDetail.deleted') }); invalidate(); navigate('/subjects'); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  if (isLoading) return <BlockLoader variant="card" />;
  if (!subject) return <Center h={300}><Text c="dimmed">{t('subjectDetail.notFound')}</Text></Center>;

  return (
    <Stack maw={640}>
      <Group>
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/subjects')}><IconArrowLeft size={18} /></ActionIcon>
        <Group gap="xs"><SubjectIconView icon={subject.icon} suggested={subject.suggestedIcon} size={22} /><Title order={4}>{subject.name}</Title></Group>
      </Group>

      {/* Ishlatilishi */}
      <SimpleGrid cols={2}>
        <Card><Text size="xl" fw={700}>{subject._count?.schedule ?? 0}</Text><Text size="sm" c="dimmed">{t('subjectDetail.inSchedule')}</Text></Card>
        <Card><Text size="xl" fw={700}>{subject._count?.homeworks ?? 0}</Text><Text size="sm" c="dimmed">{t('subjectDetail.inHomework')}</Text></Card>
      </SimpleGrid>

      {/* Tahrirlash */}
      <SectionCard title={t('subjectDetail.edit')} icon={IconPencil}>
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput label={t('subjectsPage.nameUz')} required {...form.getInputProps('name')} />
            <TextInput label={t('subjectsPage.nameRu')} {...form.getInputProps('nameRu')} />
            <TextInput label={t('subjectsPage.nameEn')} {...form.getInputProps('nameEn')} />
            <TextInput label={t('qc.code')} {...form.getInputProps('code')} />
            {/* `icon: null` PATCH'da ataylab YUBORILADI — server uni "tozalash" deb
                tushunadi va fan yana taklif bo'yicha ko'rinadi (kalit umuman
                yuborilmasa maydonga tegilmasdi — tanlovni bekor qilib bo'lmasdi). */}
            <SubjectIconPicker
              value={form.values.icon}
              onChange={(v) => form.setFieldValue('icon', v)}
              suggested={subject.suggestedIcon}
            />
            <Button type="submit" leftSection={<IconDeviceFloppy size={16} />} loading={saveMut.isPending}>{t('common.save')}</Button>
          </Stack>
        </form>
      </SectionCard>

      {/* O'chirish */}
      <Card withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">{t('subjectDetail.deleteHint')}</Text>
          <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={() => confirmDelete(subject.name, () => deleteMut.mutate())} loading={deleteMut.isPending}>
            {t('common.delete')}
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}
